// @ts-ignore
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Nav,
  Tab,
  Alert,
  ProgressBar
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faPlus,
  faEdit,
  faTrash,
  faCopy,
  faUsers,
  faChartLine,
  faDollarSign,
  faPercent,
  faCalendarAlt,
  faHistory,
  faCrown,
  faBuilding,
  faUserGraduate,
  faRocket,
  faCheckCircle,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

interface CommissionTemplate {
  id: string;
  name: string;
  description: string;
  baseAgentSplit: number;
  baseBrokerSplit: number;
  transactionFee: number;
  eoFee: number;
  monthlyFee: number;
  platformMonthlyFee: number;
  agentMonthlyFee: number;
  supportsVolumeTiers: boolean;
  allowsPartnerSplits: boolean;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  agentCount: number;
  tiers?: CommissionTier[];
}

interface CommissionTier {
  id: string;
  tierName: string;
  minGci: number;
  maxGci?: number;
  minTransactions: number;
  maxTransactions?: number;
  agentSplit: number;
  brokerSplit: number;
  transactionFee: number;
}

const CommissionTemplates: React.FC = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<CommissionTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<CommissionTemplate | null>(null);

  // Get brokerage ID from localStorage
  const brokerageId = localStorage.getItem('brokerageId');

  useEffect(() => {
    fetchTemplates();
  }, [brokerageId]);

  const fetchTemplates = async () => {
    if (!brokerageId) {
      setError('No brokerage ID found');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/commission-plans/templates/${brokerageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch templates');
      }

      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
      } else {
        throw new Error(data.message || 'Failed to load templates');
      }
    } catch (err: any) {
      console.error('Error fetching templates:', err);
      setError(err.message || 'Failed to load commission templates');
    } finally {
      setLoading(false);
    }
  };

  const createTemplate = async (templateData: any) => {
    try {
      const response = await fetch('/api/commission-plans/templates', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          brokerageId,
          ...templateData
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create template');
      }

      const data = await response.json();
      if (data.success) {
        await fetchTemplates(); // Refresh list
        setShowCreateModal(false);
        alert('Template created successfully!');
      } else {
        throw new Error(data.message || 'Failed to create template');
      }
    } catch (err: any) {
      console.error('Error creating template:', err);
      alert(`Failed to create template: ${err.message}`);
    }
  };

  const getPlanIcon = (template: CommissionTemplate) => {
    if (template.supportsVolumeTiers) return faRocket;
    if (template.baseAgentSplit >= 90) return faCrown;
    if (template.baseAgentSplit >= 85) return faUserGraduate;
    return faBuilding;
  };

  const getPlanColor = (template: CommissionTemplate) => {
    if (template.supportsVolumeTiers) return 'warning';
    if (template.baseAgentSplit >= 90) return 'success';
    if (template.baseAgentSplit >= 85) return 'primary';
    return 'secondary';
  };

  if (loading) {
    return (
      <Container fluid className="p-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-muted">Loading commission templates...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h4 className="mb-1">
                <FontAwesomeIcon icon={faFileContract} className="me-2 text-primary" />
                Commission Plan Templates
              </h4>
              <p className="text-muted mb-0">
                Create and manage commission structures for your agents
              </p>
            </div>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              className="d-flex align-items-center gap-2"
            >
              <FontAwesomeIcon icon={faPlus} />
              Create New Template
            </Button>
          </div>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Total Templates</p>
                  <h3 className="mb-0">{templates.length}</h3>
                </div>
                <FontAwesomeIcon icon={faFileContract} size="2x" className="text-primary opacity-25" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Agents Covered</p>
                  <h3 className="mb-0">{templates.reduce((sum, t) => sum + t.agentCount, 0)}</h3>
                </div>
                <FontAwesomeIcon icon={faUsers} size="2x" className="text-success opacity-25" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Avg Agent Split</p>
                  <h3 className="mb-0">
                    {Math.round(templates.reduce((sum, t) => sum + t.baseAgentSplit, 0) / templates.length)}%
                  </h3>
                </div>
                <FontAwesomeIcon icon={faPercent} size="2x" className="text-info opacity-25" />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1">Volume-Based Plans</p>
                  <h3 className="mb-0">{templates.filter(t => t.supportsVolumeTiers).length}</h3>
                </div>
                <FontAwesomeIcon icon={faChartLine} size="2x" className="text-warning opacity-25" />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Templates Grid */}
      <Row className="g-4">
        {templates.map(template => (
          <Col lg={6} key={template.id}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Header className="bg-white border-0 pb-0">
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: '48px',
                        height: '48px',
                        background: `var(--bs-${getPlanColor(template)}-bg-subtle)`
                      }}
                    >
                      <FontAwesomeIcon 
                        icon={getPlanIcon(template)} 
                        className={`text-${getPlanColor(template)}`}
                        size="lg"
                      />
                    </div>
                    <div>
                      <h5 className="mb-1">
                        {template.name}
                        {template.isDefault && (
                          <Badge bg="info" className="ms-2" style={{ fontSize: '0.7rem' }}>
                            Default
                          </Badge>
                        )}
                      </h5>
                      <p className="text-muted mb-0">{template.description}</p>
                    </div>
                  </div>
                  <div className="d-flex gap-1">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => setSelectedTemplate(template)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  </div>
                </div>
              </Card.Header>
              <Card.Body>
                {/* Base Commission Structure */}
                <div className="mb-3">
                  <h6 className="text-muted mb-2">Base Commission Structure</h6>
                  <Row className="g-3">
                    <Col xs={6}>
                      <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                        <span className="text-muted">Agent Split</span>
                        <Badge bg="success">{template.baseAgentSplit}%</Badge>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                        <span className="text-muted">Broker Split</span>
                        <Badge bg="primary">{template.baseBrokerSplit}%</Badge>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                        <span className="text-muted">Transaction Fee</span>
                        <span className="fw-semibold">${template.transactionFee}</span>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="d-flex justify-content-between align-items-center p-2 rounded bg-light">
                        <span className="text-muted">Monthly Fee</span>
                        <span className="fw-semibold">${template.agentMonthlyFee || template.monthlyFee}</span>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Volume Tiers */}
                {template.supportsVolumeTiers && template.tiers && (
                  <div className="mb-3">
                    <h6 className="text-muted mb-2">Performance Tiers</h6>
                    <div className="small">
                      {template.tiers.map((tier, index) => (
                        <div key={tier.id} className="d-flex align-items-center mb-2">
                          <Badge 
                            bg={index === 0 ? 'secondary' : index === 1 ? 'primary' : 'success'}
                            className="me-2"
                          >
                            {tier.tierName}
                          </Badge>
                          <span className="text-muted">
                            ${tier.minGci.toLocaleString()}+ GCI • 
                            {tier.agentSplit}% split • 
                            ${tier.transactionFee} fee
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Footer Stats */}
                <div className="d-flex justify-content-between align-items-center pt-3 border-top">
                  <div className="d-flex align-items-center gap-3">
                    <span className="text-muted">
                      <FontAwesomeIcon icon={faUsers} className="me-1" />
                      {template.agentCount} agents
                    </span>
                    <span className="text-muted">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
                      Created {new Date(template.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <Button
                    variant="outline-success"
                    size="sm"
                    onClick={() => {
                      // Navigate to agent-plans sub-route with filter
                      navigate('/broker-intelligence/commission-plans/agent-plans?filter=' + encodeURIComponent(template.name));
                    }}
                  >
                    View Agents
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Create Template Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faFileContract} className="me-2" />
            Create Commission Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={(e) => {
            e.preventDefault();
            const formData = new FormData(e.currentTarget);
            createTemplate({
              templateName: formData.get('templateName'),
              description: formData.get('description'),
              baseRates: {
                agentSplit: Number(formData.get('agentSplit')),
                brokerSplit: Number(formData.get('brokerSplit')),
                transactionFee: Number(formData.get('transactionFee')),
                eoFee: Number(formData.get('eoFee')),
                platformMonthlyFee: Number(formData.get('platformMonthlyFee')),
                agentMonthlyFee: Number(formData.get('agentMonthlyFee'))
              },
              tierSettings: {
                allowsPartnerSplits: formData.get('allowsPartnerSplits') === 'on'
              }
            });
          }}>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Template Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="templateName"
                    placeholder="e.g., Standard Agent Plan"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Group>
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="description"
                    rows={2}
                    placeholder="Brief description of this commission plan"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Agent Split (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="agentSplit"
                    min="0"
                    max="100"
                    defaultValue="80"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Broker Split (%)</Form.Label>
                  <Form.Control
                    type="number"
                    name="brokerSplit"
                    min="0"
                    max="100"
                    defaultValue="20"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Transaction Fee ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="transactionFee"
                    min="0"
                    defaultValue="295"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>E&O Insurance Fee ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="eoFee"
                    min="0"
                    defaultValue="60"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Platform Monthly Fee ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="platformMonthlyFee"
                    min="0"
                    defaultValue="70"
                    required
                  />
                  <Form.Text className="text-muted">
                    KairosIQ platform fee charged to brokerage
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Agent Monthly Fee ($)</Form.Label>
                  <Form.Control
                    type="number"
                    name="agentMonthlyFee"
                    min="0"
                    defaultValue="100"
                    required
                  />
                  <Form.Text className="text-muted">
                    Total fee charged to agents (Broker keeps ${(100 - 70).toFixed(2)})
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <Form.Check
                  type="checkbox"
                  name="allowsPartnerSplits"
                  label="Allow partner splits for this template"
                />
              </Col>
            </Row>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                Create Template
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Edit Template Modal */}
      <Modal 
        show={!!selectedTemplate} 
        onHide={() => setSelectedTemplate(null)} 
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faEdit} className="me-2" />
            Edit Commission Template
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTemplate && (
            <Form onSubmit={async (e) => {
              e.preventDefault();
              
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              
              try {
                const response = await fetch(`/api/commission-plans/templates/${selectedTemplate.id}`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                    'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                    templateName: formData.get('templateName') as string,
                    description: formData.get('description') as string,
                    baseRates: {
                      agentSplit: Number(formData.get('agentSplit')),
                      brokerSplit: Number(formData.get('brokerSplit')),
                      transactionFee: Number(formData.get('transactionFee')),
                      eoFee: Number(formData.get('eoFee')),
                      platformMonthlyFee: Number(formData.get('platformMonthlyFee')),
                      agentMonthlyFee: Number(formData.get('agentMonthlyFee'))
                    }
                  })
                });
                
                if (response.ok) {
                  const data = await response.json();
                  if (data.success) {
                    // Refresh templates list
                    await fetchTemplates();
                    setSelectedTemplate(null);
                    // Show success message if needed
                  } else {
                    throw new Error(data.message || 'Failed to update template');
                  }
                } else {
                  throw new Error('Failed to update commission template');
                }
              } catch (err: any) {
                console.error('Error updating template:', err);
                alert(err.message || 'Failed to update commission template');
              }
            }}>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Template Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="templateName"
                      defaultValue={selectedTemplate.name}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="description"
                      defaultValue={selectedTemplate.description}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Agent Split (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      name="agentSplit"
                      defaultValue={selectedTemplate.baseAgentSplit}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Broker Split (%)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      name="brokerSplit"
                      defaultValue={selectedTemplate.baseBrokerSplit}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Transaction Fee ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      name="transactionFee"
                      defaultValue={selectedTemplate.transactionFee}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>E&O Insurance Fee ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      name="eoFee"
                      defaultValue={selectedTemplate.eoFee}
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Platform Monthly Fee ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      name="platformMonthlyFee"
                      defaultValue={selectedTemplate.platformMonthlyFee || 70}
                      required
                    />
                    <Form.Text className="text-muted">
                      KairosIQ platform fee charged to brokerage
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Agent Monthly Fee ($)</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      name="agentMonthlyFee"
                      defaultValue={selectedTemplate.agentMonthlyFee || selectedTemplate.monthlyFee}
                      required
                    />
                    <Form.Text className="text-muted">
                      Total fee charged to agents (Broker keeps ${((selectedTemplate.agentMonthlyFee || selectedTemplate.monthlyFee) - (selectedTemplate.platformMonthlyFee || 70)) > 0 ? ((selectedTemplate.agentMonthlyFee || selectedTemplate.monthlyFee) - (selectedTemplate.platformMonthlyFee || 70)).toFixed(2) : '0'})
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col>
                  <Form.Check
                    type="checkbox"
                    defaultChecked={selectedTemplate.allowsPartnerSplits}
                    label="Allow partner splits for this template"
                  />
                </Col>
              </Row>
              <Alert variant="info">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                <strong>Note:</strong> Editing a template will only affect new agent assignments. 
                Existing agents will keep their current terms unless manually updated.
              </Alert>
              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={() => setSelectedTemplate(null)}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  Save Changes
                </Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default CommissionTemplates;